import { useEffect, useMemo, useState } from 'react';
import { PageLayout } from '@/components/layout/PageLayout';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { SessionList } from '@/components/sessions/SessionList';
import { SessionSortingFilterPopover } from '@/components/sessions/SessionSortingFilterPopover';
import { SessionStatusFilterDropdown } from '@/components/sessions/SessionStatusFilterDropdown';
import { PositionFilterDropdown } from '@/components/sessions/PositionFilterDropdown';
import { sessionService } from '@/services/sessionService';
import type { PageProps } from '@/types/page';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { SESSION_CREATION_LIMITS, type PublicSession, type SubscriptionTier } from '@arrowise/shared';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { SearchIcon } from 'lucide-react';
import { usePositionsStore } from '@/store/positionsStore';
import { Card, CardContent } from '@/components/ui/card';
import { Briefcase } from 'lucide-react';

export default function SessionsPage({ nav = true, footer = true }: PageProps) {
    const [sessions, setSessions] = useState<PublicSession[]>([]);
    const [total, setTotal] = useState(0);
    const { teams, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [sessionLimitReached, setSessionLimitReached] = useState(false);
    const [tierLimitText, setTierLimitText] = useState<string>('');
    const navigate = useNavigate();
    const [statusFilter, setStatusFilter] = useState<('incomplete' | 'complete' | 'graded')[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(25);
    const [sortBy, setSortBy] = useState<'candidate_name' | 'created_at' | 'ended_at' | 'due_at'>('created_at');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [positionId, setPositionId] = useState<string | undefined>(undefined);

    // Positions store
    const { positions, isLoading: positionsLoading, loadPositions } = usePositionsStore();

    // Function to perform search
    const performSearch = async (page: number) => {
        setLoading(true);
        try {
            const query: any = {
                status: statusFilter,
                search: searchQuery || undefined,
                page,
                limit,
                sortBy,
                sortOrder,
                positionId: positionId || undefined,
            };
            let data;
            if (teams && teams.length > 0) {
                // Grouped team sessions
                data = await sessionService.getGroupedSessionsForTeam(teams[0].team.id, query);
            } else {
                // Grouped sessions for current user
                data = await sessionService.getGroupedSessionsForUser(query);
            }
            setSessions(data.sessions);
            setTotal(data.total);
        } catch (err: any) {
            toast.error(err?.message || 'Failed to load sessions');
        } finally {
            setLoading(false);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            performSearch(1);
        }
    };

    // If user is in any team, only allow create when role is owner/admin in at least one team
    const canCreateSession = useMemo(() => {
        if (!teams || teams.length === 0) return true; // individual user
        return teams.some(t => t.role === 'owner' || t.role === 'admin');
    }, [teams]);

    // Determine session creation limit and whether user reached it
    useEffect(() => {
        if (!user) return;
        // Only applies for individual sessions (no teams)
        if (!teams || teams.length === 0) {
            const tier = (user.billingInfo?.subscriptionTier) as SubscriptionTier;
            const limit = SESSION_CREATION_LIMITS[tier];
            const currentCount = statusFilter.includes('incomplete') ? sessions.length : 0; // approximate
            const reached = limit !== 'unlimited' && currentCount >= (limit as number);
            setSessionLimitReached(reached);
            setTierLimitText(
                limit === 'unlimited'
                    ? `Your ${tier} plan allows unlimited sessions.`
                    : `Your ${tier} plan allows a maximum of ${limit} incomplete session${limit === 1 ? '' : 's'}.`
            );
        } else {
            setSessionLimitReached(false);
            setTierLimitText('');
        }
    }, [user, teams, sessions, statusFilter]);

    // Helper function to check if user can manage/delete a specific session
    const canManageSession = (session: PublicSession): boolean => {
        if (!user) return false;

        // Individual session: only creator can manage
        if (!session.teamId) {
            return session.createdBy === user.id;
        }

        // Team session: only owner/admin can manage
        const userTeam = teams?.find(t => t.team.id === session.teamId);
        return userTeam?.role === 'owner' || userTeam?.role === 'admin';
    };

    useEffect(() => {
        (async () => {
            await performSearch(currentPage);
        })();
    }, [teams, statusFilter, sortBy, sortOrder, currentPage, positionId]);

    // Load positions on mount
    useEffect(() => {
        loadPositions();
    }, [loadPositions]);

    // Handle case where ended_at sort is selected but status changes to incomplete
    useEffect(() => {
        if (statusFilter.includes('incomplete') && sortBy === 'ended_at') {
            setSortBy('created_at'); // Switch to a valid sort field
        }
    }, [statusFilter, sortBy]);

    function handleManage(id: string) {
        navigate(`/app/sessions/${id}`);
    }

    async function handleDelete(id: string) {
        try {
            await sessionService.deleteSession(id);
            setSessions((prev) => prev.filter((s) => s.id !== id));
            toast.success('Session deleted');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to delete session');
        }
    }

    function handleView(id: string) {
        navigate(`/app/sessions/${id}/view`);
    }

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-6xl w-full space-y-8">
                <div className="flex items-end justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Sessions</h1>
                        <p className="text-sm text-muted-foreground mt-1">Create and manage your interview sessions.</p>
                    </div>
                    <div className="flex gap-2">
                        {canCreateSession && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        {sessionLimitReached || positions.length === 0 ? (
                                            <span>
                                                <Button disabled>Create Session</Button>
                                            </span>
                                        ) : (
                                            <Link to="/app/sessions/create">
                                                <Button>Create Session</Button>
                                            </Link>
                                        )}
                                    </TooltipTrigger>
                                    {sessionLimitReached && positions.length > 0 && (
                                        <TooltipContent>{tierLimitText}</TooltipContent>
                                    )}
                                    {positions.length === 0 && !positionsLoading && (
                                        <TooltipContent>No positions available. Create a position first.</TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        <Link to="/app/session-templates">
                            <Button variant="outline">View Templates</Button>
                        </Link>
                    </div>
                </div>

                <div className="flex gap-4 items-center justify-between">
                    <div className="flex gap-2 w-1/3">
                        <Input
                            placeholder="Search by candidate name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={handleKeyPress}
                            className="flex-1"
                            disabled={positions.length === 0 && !positionsLoading}
                        />
                        <Button
                            variant="secondary"
                            onClick={() => performSearch(1)}
                            className="shrink-0"
                            disabled={positions.length === 0 && !positionsLoading}
                        >
                            <SearchIcon className="h-4 w-4" />
                            Search
                        </Button>
                    </div>
                    <div className="flex gap-2 items-center">
                        <PositionFilterDropdown
                            positionId={positionId}
                            onPositionFilterChange={setPositionId}
                            disabled={positions.length === 0 && !positionsLoading}
                        />
                        <SessionSortingFilterPopover
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            statusFilter={statusFilter}
                            onSortChange={(newSortBy, newSortOrder) => {
                                setSortBy(newSortBy);
                                setSortOrder(newSortOrder);
                            }}
                            disabled={positions.length === 0 && !positionsLoading}
                        />
                        <SessionStatusFilterDropdown
                            statusFilter={statusFilter}
                            onStatusFilterChange={setStatusFilter}
                            disabled={positions.length === 0 && !positionsLoading}
                        />
                    </div>
                </div>

                <div className="space-y-6">
                    {loading ? (
                        <div className="flex flex-col gap-3">
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                            <Skeleton className="h-24" />
                        </div>
                    ) : positions.length === 0 && !positionsLoading ? (
                        <Card>
                            <CardContent className="py-10 text-center text-muted-foreground">
                                <Briefcase className="mx-auto h-12 w-12 mb-4 opacity-50" />
                                <h3 className="text-lg font-medium mb-2">No positions found</h3>
                                <p className="mb-4">
                                    You need to create at least one position before you can create interview sessions.
                                </p>
                                <Link to="/app/positions">
                                    <Button>
                                        <Briefcase className="h-4 w-4 mr-2" />
                                        Create Your First Position
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>
                    ) : (
                        <SessionList
                            sessions={sessions}
                            onManage={handleManage}
                            onDelete={handleDelete}
                            onView={handleView}
                            canManageSession={canManageSession}
                            total={total}
                            currentPage={currentPage}
                            limit={limit}
                            onPageChange={handlePageChange}
                            isFreeUser={user?.billingInfo?.subscriptionTier === 'free'}
                        />
                    )}
                </div>
            </div>
        </PageLayout>
    );
}
